// @ts-check
// Diálogos de nota fiscal do painel de suporte: agendar (e emitir) a NFS-e de uma cobrança e configurar a nota
// automática da assinatura de uma instituição. Os impostos vão em percentual, como o Asaas pede; o serviço municipal
// vem da lista cadastrada na conta do Asaas.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { fetchAdminMunicipalServices, saveAdminInvoiceSettings, scheduleAdminInvoice } from '../../../data/admin-finance.js';
import { normalizeInvoiceTaxes } from '../../../lib/admin-navigation.js';
import { todayIsoDate } from '../../../lib/format.js';
import { Modal } from '../../../ui/modal.js';
import { Checkbox } from '../../../ui/feedback.js';
import { showSuccessSnackbar } from '../../../ui/snackbar.js';
import { FormError, SelectField, TextField } from '../../settings/integration-ui.js';
import { operationsErrorMessage } from '../operations-format.js';
import { formatMoney } from './admin-ui.js';
import { t } from '../../../i18n/index.js';

const TAX_FIELDS = /** @type {const} */ (['iss', 'pis', 'cofins', 'csll', 'inss', 'ir']);
const INVOICE_PERIODS = ['ON_PAYMENT_CONFIRMATION', 'ON_PAYMENT_DUE_DATE', 'BEFORE_PAYMENT_DUE_DATE', 'ON_DUE_DATE_MONTH', 'ON_NEXT_MONTH'];
const DAYS_BEFORE_DUE_DATE = ['5', '10', '15', '30', '60'];

/** @param {string} key @param {Record<string, unknown>} [values] */
const text = (key, values) => t(`operations.admin.panel.invoiceForm.${key}`, values);

/**
 * Serviço municipal da conta do Asaas, escolhido numa lista; sem serviços cadastrados o nome é digitado.
 * @param {{ value: { id: string | null, description: string, issTax?: number | null }, onChange: (value: { id: string | null, description: string, issTax?: number | null }) => void }} props
 */
function MunicipalServiceField({ value, onChange }) {
  const [services, setServices] = useState(/** @type {Array<{ id: string, description: string, issTax: number }> | null} */ (null));
  useEffect(() => {
    fetchAdminMunicipalServices().then((result) => setServices(result.services)).catch(() => setServices([]));
  }, []);
  if (services === null) return html`<p class="admin-muted">${text('loadingServices')}</p>`;
  if (services.length === 0) {
    return html`<${TextField} label=${text('municipalService')} value=${value.description} hint=${text('municipalServiceHint')} isRequired=${true}
      onInput=${(/** @type {string} */ description) => onChange({ id: null, description })} />`;
  }
  return html`<${SelectField} label=${text('municipalService')} value=${value.id ?? ''} hint=${text('municipalServiceListHint')}
    onChange=${(/** @type {string} */ id) => {
      const service = services.find((candidate) => candidate.id === id);
      onChange(service ? { id: service.id, description: service.description, issTax: service.issTax } : { id: null, description: '' });
    }}
    options=${[{ value: '', label: text('chooseService') }, ...services.map((service) => ({ value: service.id, label: `${service.description} (ISS ${service.issTax}%)` }))]} />`;
}

/**
 * @param {{ taxes: Record<string, string | boolean>, onChange: (taxes: Record<string, string | boolean>) => void }} props
 */
function TaxesFields({ taxes, onChange }) {
  return html`<div class="admin-taxes">
    <span class="admin-filter-label">${text('taxes')}</span>
    <div class="admin-taxes-grid">
      ${TAX_FIELDS.map((name) => html`<label key=${name} class="admin-tax-field">
        <span>${text(`tax.${name}`)}</span>
        <input class="text-input" type="text" inputmode="decimal" value=${String(taxes[name] ?? '')} placeholder="0"
          onInput=${(/** @type {Event} */ event) => onChange({ ...taxes, [name]: /** @type {HTMLInputElement} */ (event.currentTarget).value })} />
      </label>`)}
    </div>
    <label class="admin-check-field">
      <${Checkbox} checked=${taxes.retainIss === true} onChange=${(/** @type {boolean} */ checked) => onChange({ ...taxes, retainIss: checked })} ariaLabel=${text('retainIss')} />
      <span>${text('retainIss')}</span>
    </label>
    <p class="admin-field-hint">${text('taxesHint')}</p>
  </div>`;
}

/**
 * Agenda a nota de uma cobrança; "Emitir agora" já pede a autorização ao Asaas.
 * @param {{ payment: { id: string, value: number, description?: string | null, owner?: any }, onClose: () => void, onScheduled: (invoice: any) => void }} props
 */
export function ScheduleInvoiceDialog({ payment, onClose, onScheduled }) {
  const [service, setService] = useState(/** @type {{ id: string | null, description: string, issTax?: number | null }} */ ({ id: null, description: '' }));
  const [serviceDescription, setServiceDescription] = useState(text('defaultServiceDescription', { description: payment.description ?? '' }).trim());
  const [observations, setObservations] = useState('');
  const [value, setValue] = useState(String(payment.value ?? ''));
  const [effectiveDate, setEffectiveDate] = useState(todayIsoDate());
  const [taxes, setTaxes] = useState(/** @type {Record<string, string | boolean>} */ ({ retainIss: false }));
  const [authorize, setAuthorize] = useState(true);
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [isSaving, setIsSaving] = useState(false);

  // a alíquota do serviço escolhido preenche o ISS quando o campo ainda está vazio
  const chooseService = (/** @type {typeof service} */ next) => {
    setService(next);
    if (next.issTax !== undefined && next.issTax !== null && !taxes.iss) setTaxes({ ...taxes, iss: String(next.issTax) });
  };

  const submit = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const { invoice } = await scheduleAdminInvoice({
        paymentId: payment.id, serviceDescription: serviceDescription.trim(), observations: observations.trim(), municipalServiceId: service.id, municipalServiceName: service.description,
        value: Number(String(value).replace(',', '.')), effectiveDate, taxes: normalizeInvoiceTaxes(taxes), authorize,
      });
      showSuccessSnackbar(text(authorize ? 'issued' : 'scheduled'));
      onScheduled(invoice);
    } catch (requestError) {
      setError(operationsErrorMessage(requestError));
    } finally {
      setIsSaving(false);
    }
  };

  return html`<${Modal} title=${text('scheduleTitle')} subtitle=${text('scheduleSubtitle', { value: formatMoney(payment.value) })} confirmLabel=${authorize ? text('issueNow') : text('scheduleOnly')}
    isLoading=${isSaving} isConfirmDisabled=${!service.description.trim() || !serviceDescription.trim()} onClose=${onClose} onConfirm=${submit}>
    <div class="admin-form">
      <${MunicipalServiceField} value=${service} onChange=${chooseService} />
      <${TextField} label=${text('serviceDescription')} value=${serviceDescription} onInput=${setServiceDescription} isRequired=${true} multiline=${true} />
      <div class="admin-form-row">
        <${TextField} label=${text('value')} value=${value} onInput=${setValue} hint=${text('valueHint')} />
        <${TextField} label=${text('effectiveDate')} value=${effectiveDate} onInput=${setEffectiveDate} type="date" />
      </div>
      <${TaxesFields} taxes=${taxes} onChange=${setTaxes} />
      <${TextField} label=${text('observations')} value=${observations} onInput=${setObservations} multiline=${true} hint=${text('observationsHint')} />
      <label class="admin-check-field">
        <${Checkbox} checked=${authorize} onChange=${setAuthorize} ariaLabel=${text('authorizeNow')} />
        <span>${text('authorizeNow')}</span>
      </label>
      <${FormError} message=${error} />
    </div>
  </${Modal}>`;
}

/**
 * Nota automática da assinatura: o Asaas emite a nota de cada cobrança no momento escolhido.
 * @param {{ tenantId: string, existing: any | null, onClose: () => void, onSaved: (settings: any) => void }} props
 */
export function InvoiceSettingsDialog({ tenantId, existing, onClose, onSaved }) {
  const [service, setService] = useState(/** @type {{ id: string | null, description: string, issTax?: number | null }} */ ({ id: existing?.municipalServiceId ?? null, description: existing?.municipalServiceName ?? '' }));
  const [period, setPeriod] = useState(existing?.invoiceCreationPeriod ?? 'ON_PAYMENT_CONFIRMATION');
  const [daysBeforeDueDate, setDaysBeforeDueDate] = useState(String(existing?.daysBeforeDueDate ?? '5'));
  const [receivedOnly, setReceivedOnly] = useState(existing?.receivedOnly === true);
  const [observations, setObservations] = useState(existing?.observations ?? '');
  const [taxes, setTaxes] = useState(/** @type {Record<string, string | boolean>} */ (
    existing?.taxes ? Object.fromEntries(Object.entries(existing.taxes).map(([key, value]) => [key, key === 'retainIss' ? value === true : String(value ?? '')])) : { retainIss: false }
  ));
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [isSaving, setIsSaving] = useState(false);

  const chooseService = (/** @type {typeof service} */ next) => {
    setService(next);
    if (next.issTax !== undefined && next.issTax !== null && !taxes.iss) setTaxes({ ...taxes, iss: String(next.issTax) });
  };

  const submit = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const { invoiceSettings } = await saveAdminInvoiceSettings(tenantId, {
        ...(existing ? {} : { municipalServiceId: service.id, municipalServiceName: service.description }),
        effectiveDatePeriod: period, ...(period === 'BEFORE_PAYMENT_DUE_DATE' ? { daysBeforeDueDate: Number(daysBeforeDueDate) } : {}), receivedOnly,
        observations: observations.trim(), taxes: normalizeInvoiceTaxes(taxes),
      });
      showSuccessSnackbar(text('settingsSaved'));
      onSaved(invoiceSettings);
    } catch (requestError) {
      setError(operationsErrorMessage(requestError));
    } finally {
      setIsSaving(false);
    }
  };

  return html`<${Modal} title=${existing ? text('settingsEditTitle') : text('settingsTitle')} subtitle=${text('settingsSubtitle')} confirmLabel=${t('common.save')}
    isLoading=${isSaving} isConfirmDisabled=${!existing && !service.description.trim()} onClose=${onClose} onConfirm=${submit}>
    <div class="admin-form">
      ${existing
        ? html`<p class="admin-muted">${text('settingsServiceFixed', { service: existing.municipalServiceName ?? '' })}</p>`
        : html`<${MunicipalServiceField} value=${service} onChange=${chooseService} />`}
      <${SelectField} label=${text('period')} value=${period} onChange=${setPeriod} options=${INVOICE_PERIODS.map((value) => ({ value, label: text(`periods.${value}`) }))} />
      ${period === 'BEFORE_PAYMENT_DUE_DATE'
        ? html`<${SelectField} label=${text('daysBeforeDueDate')} value=${daysBeforeDueDate} onChange=${setDaysBeforeDueDate} options=${DAYS_BEFORE_DUE_DATE.map((value) => ({ value, label: text('days', { count: Number(value) }) }))} />`
        : null}
      <label class="admin-check-field">
        <${Checkbox} checked=${receivedOnly} onChange=${setReceivedOnly} ariaLabel=${text('receivedOnly')} />
        <span>${text('receivedOnly')}</span>
      </label>
      <${TaxesFields} taxes=${taxes} onChange=${setTaxes} />
      <${TextField} label=${text('observations')} value=${observations} onInput=${setObservations} multiline=${true} hint=${text('observationsHint')} />
      <${FormError} message=${error} />
    </div>
  </${Modal}>`;
}
